import { useState, useEffect, useMemo } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'

import { Loading, ErrorPage } from '../../components'
import { withRouterProps } from '../../utils/wrappers'
import ChatView from './ChatView'
import ChatInput from './ChatInput'
import useSalaryPrompt from './useSalaryPrompt'
import useSpouseGoalPrompt from './useSpouseGoalPrompt'
import { botTurn } from './chatTurns'
import { actionItemText, promptForKey } from '../home/heyNic/homeChatDefaults'
import { HeyNicWrapper } from './styled'

/*
 * Hey Nic main content area (transcript + input footer). The focused top nav
 * (HeyNicTopNav) and chat-history sidebar (ChatHistorySidebar) are rendered by
 * the Main layout's routed slots, so entering /hey-nic morphs the shell in
 * place without the Hey Nic graphic moving horizontally.
 */
const HeyNic = ({ store, navigate, location, inputRef }) => {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState(false)
  const [draft, setDraft] = useState('')

  const aiStatus = store.aiStatus

  /*
   * An opening comment handed over by the entry point that sent the user here —
   * today that's Home's comment card, which shows the agent's remark in-page and
   * carries it into the chat so tapping the card doesn't drop the thing the user
   * tapped ON.
   *
   * Client-only, exactly like the SBSA panel's: it is not persisted, not sent to
   * the agent, and it survives the post-stream reconcile.
   *
   * It belongs to ONE chat and must be scoped to it, because `activeChat` is a
   * single slot that moves underneath this component in two ways:
   *
   *   - Entry itself may not land on a blank chat at all. Leaving mid-response
   *     deliberately keeps the stream running, and `resolveAndOpenInitialChat`
   *     returns that live conversation as-is rather than creating anything (it
   *     would have to abort the reply to do otherwise).
   *   - The Recents rail and New chat both call openChat/startNewChat while this
   *     stays mounted.
   *
   * Unscoped, either one leaves the comment prepended to a transcript it has
   * nothing to do with — the user taps a Home alert and reads it as the opening
   * line of an unrelated conversation.
   */
  const leadingText = location?.state?.leading
  // The entry point's starter pills, if it sent any. Same handoff, same scoping:
  // they belong to that comment and go stale with it.
  const leadingSuggestions = location?.state?.leadingSuggestions
  /*
   * The entry point handed over the placeholder-salary alert, whose pills this
   * route answers itself rather than sending to the agent — the same exchange
   * the Home panel runs, so tapping "Learn more" gives the same approved copy
   * whichever width the user is on (see salaryPrompt).
   */
  /*
   * The help-bearing alerts that were live at entry, in the order they take the
   * pills — the whole queue, not just the winner.
   *
   * This route has no alert logic of its own; everything above is a snapshot of
   * the moment the user tapped through. That is fine for the opener, but not for
   * ownership: finishing the salary exchange in here clears its alert, and
   * without the queue the alert behind it could never take the pills over, so
   * its help stayed unreachable for the rest of the conversation.
   */
  const leadingPromptKeys = location?.state?.promptKeys ?? []
  const [handoffChatId, setHandoffChatId] = useState(null)
  // Stamped once per entry so it doesn't tick over on re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openedAt = useMemo(() => new Date().toISOString(), [leadingText])
  /*
   * Keyed on the chat id rather than cleared on the first swap away: the comment
   * is that chat's opener, so coming back to it (via Recents, or New chat
   * reopening the same still-blank chat) should show it again — the same way the
   * SBSA step comment returns when you return to its step. Sending doesn't
   * revoke it either; it stays above the user's first message, where an opener
   * belongs.
   */
  const adopted = handoffChatId != null && store.activeChat?.id === handoffChatId

  /*
   * How far down that queue we are. Advanced when the owning exchange finishes,
   * which is the only handover this route can observe.
   */
  const [handedOver, setHandedOver] = useState(0)
  const currentPrompt = promptForKey(leadingPromptKeys[handedOver])?.prompt ?? null

  const salary = useSalaryPrompt({
    // Only while the handed-over comment is on screen: once it is scoped away
    // (a different chat), its pills are gone and so is the exchange they start.
    enabled: currentPrompt === 'salary' && adopted,
    onSave: async amount => {
      await store.updateAccount({ annualSalary: amount })
      await store.getSSBenefits()
      await store.getBaseCase()
    },
  })

  // Gated the same way, and mutually exclusive with the salary exchange: one
  // owner at a time, because both label a pill "Learn more".
  const spouseGoal = useSpouseGoalPrompt({ enabled: currentPrompt === 'spouseGoal' && adopted })

  /*
   * Announce the next alert once the salary exchange has actually saved.
   *
   * As its own turn, not by rewriting the opener: the opener belongs to the
   * conversation it started, and ChatView offers pills on the NEWEST turn only,
   * so anything the newcomer carries has to arrive last or it cannot be reached.
   * `saved` rather than `saving` — the confirmation batches with it, so the
   * handover is stamped after the user has been told the save worked.
   */
  const [handoverTurns, setHandoverTurns] = useState([])

  useEffect(() => {
    if (currentPrompt !== 'salary' || !salary.saved) return
    const next = promptForKey(leadingPromptKeys[handedOver + 1])
    setHandedOver(handedOver + 1)
    if (next)
      setHandoverTurns(prev => [...prev, botTurn(actionItemText(next.key), next.suggestions)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrompt, salary.saved])

  /*
   * Ordered by when each turn was made, so a handover sits after the exchange it
   * followed and before the one it starts (see HomeHeyNicChat, which merges the
   * same three sources the same way).
   */
  const clientTurns = [...salary.turns, ...spouseGoal.turns, ...handoverTurns].sort((a, b) =>
    a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
  )

  const leadingTurns = adopted
    ? [
        { role: 'bot', text: leadingText, timestamp: openedAt, suggestions: leadingSuggestions },
        ...clientTurns,
      ]
    : []

  // Availability gate: bounce home if the AI service is known to be unavailable.
  useEffect(() => {
    if (aiStatus.initialized && !aiStatus.available) {
      navigate('/home', { replace: true })
    }
  }, [aiStatus.initialized, aiStatus.available, navigate])

  // Entry lifecycle: open the most-recent blank chat or start a fresh one.
  useEffect(() => {
    let cancelled = false
    if (aiStatus.initialized && !aiStatus.available) return undefined

    store
      .resolveAndOpenInitialChat()
      .then(chat => {
        if (cancelled) return
        /*
         * Adopt the handed-over comment only if this entry actually resolved to
         * an empty chat. A chat with a conversation already in it is the
         * mid-stream case above, and prepending the comment there would be
         * inventing a first turn for someone else's transcript.
         *
         * Checked once, here, rather than on every render: once adopted the
         * comment has to survive the user sending into this chat, which is the
         * moment it stops being blank.
         */
        if (leadingText && chat?.isBlank) setHandoffChatId(chat.id ?? null)
        setReady(true)
      })
      .catch(err => {
        // The recents fetch / openChat / createChat failed — fail visibly
        // instead of hanging on the loader.
        console.error('[HeyNic] initial chat load failed:', err)
        if (!cancelled) setError(true)
      })

    return () => {
      cancelled = true
      // Intentionally do NOT abort an in-flight stream on exit. Leaving Hey Nic
      // lets the response keep streaming in the background so it's saved and
      // waiting (in Recents, or the same chat) when the user returns — see
      // resolveAndOpenInitialChat.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Clicking a suggestion submits it directly (mirrors ChatInput's submit),
  // rather than just populating the input for the user to send manually.
  const handleSuggestion = text => {
    const trimmed = text.trim()
    if (!trimmed || store.responseLoading) return
    // Both exchanges are answered client-side; only what they decline is sent.
    if (!salary.handle(trimmed) && !spouseGoal.handle(trimmed)) store.sendMessage(trimmed)
    setDraft('')
    // Keep the cursor in the textarea so the user can immediately type a
    // follow-up. Clicking a pill moves focus to the button, and ChatInput only
    // re-focuses on mount / chat-id change, so restore it explicitly here.
    inputRef.current?.focus()
  }

  if (error) return <ErrorPage />
  if (!ready) return <Loading />

  return (
    <HeyNicWrapper>
      {/* With an opening comment there is already something to read, so the
          brand-new-chat greeting would be a second, redundant opener. */}
      <ChatView
        onSuggestion={handleSuggestion}
        leadingTurns={leadingTurns}
        hideEmptyState={leadingTurns.length > 0}
        pillsDisabled={salary.saving}
      />
      {/* onSubmit so a typed answer to "what's your income?" is intercepted too,
          not just a pill tap; without it ChatInput sends straight to the agent. */}
      <ChatInput text={draft} setText={setDraft} inputRef={inputRef} onSubmit={handleSuggestion} />
    </HeyNicWrapper>
  )
}

HeyNic.propTypes = {
  /** Owned by HeyNicChat so the mobile Recents sheet can focus the input too. */
  inputRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
}

export default withRouterProps(inject('store')(observer(HeyNic)))
